import { useMemo } from 'react'
import {
  useBlacklistSummary,
  useChannels,
  useNextScreening,
  usePriceRecommendationMetrics,
  useRateScreenerMetrics,
  useSentinelChannels,
  useSentinelMetrics,
  useSettingsRows,
} from '@/api/queries'
import { buildAttention, rateScreenerPolicies, sentinelPolicies, type AttentionItem } from './attention'

/** Same windows as the dashboard defaults, so the popup and the widgets share one query each. */
export const ATTENTION_CHANNEL_HOURS = 12
const PRICE_RECOMMENDATION_HOURS = 24

export interface AttentionState {
  items: AttentionItem[]
  loading: boolean
}

/** Gathers what the dashboard already loads, plus the policy tables, and turns it into the "needs attention" list. */
export function useAttention(): AttentionState {
  const rateScreener = useRateScreenerMetrics()
  const channels = useChannels(ATTENTION_CHANNEL_HOURS)
  const nextScreening = useNextScreening()
  const channelPolicies = useSettingsRows('channel-policies')
  const bookingEnginePolicies = useSettingsRows('booking-engine-policies')
  const sentinel = useSentinelMetrics()
  const sentinelChannels = useSentinelChannels()
  const sentinelChannelPolicies = useSettingsRows('sentinel-channel-policies')
  const priceReco = usePriceRecommendationMetrics(PRICE_RECOMMENDATION_HOURS)
  const blacklist = useBlacklistSummary()

  const sources = [
    { name: 'RateScreener metrics', query: rateScreener },
    { name: 'RateScreener sites', query: channels },
    { name: 'Next synchronisation', query: nextScreening },
    { name: 'RateScreener policies', query: channelPolicies },
    { name: 'Booking engine policies', query: bookingEnginePolicies },
    { name: 'Sentinel metrics', query: sentinel },
    { name: 'Sentinel sites', query: sentinelChannels },
    { name: 'Sentinel policies', query: sentinelChannelPolicies },
    { name: 'Price recommendation metrics', query: priceReco },
    { name: 'Blacklisted IPs', query: blacklist },
  ]
  const unavailableKey = sources.filter((s) => s.query.isError).map((s) => s.name).join('|')

  const items = useMemo(
    () =>
      buildAttention({
        rateScreener: rateScreener.data,
        channels: channels.data,
        nextScreening: nextScreening.data,
        rateScreenerPolicies: rateScreenerPolicies(channelPolicies.data, bookingEnginePolicies.data),
        sentinel: sentinel.data,
        sentinelChannels: sentinelChannels.data,
        sentinelPolicies: sentinelPolicies(sentinelChannelPolicies.data),
        priceReco: priceReco.data,
        blacklist: blacklist.data,
        unavailable: unavailableKey ? unavailableKey.split('|') : [],
      }),
    [
      rateScreener.data,
      channels.data,
      nextScreening.data,
      channelPolicies.data,
      bookingEnginePolicies.data,
      sentinel.data,
      sentinelChannels.data,
      sentinelChannelPolicies.data,
      priceReco.data,
      blacklist.data,
      unavailableKey,
    ],
  )

  return { items, loading: sources.some((s) => s.query.isLoading) }
}
